// frontend/src/components/IndicatorChooser.tsx

import React, { useState, useRef, useEffect } from 'react';
import { useAppStore } from '../state/store';

type IndicatorChooserProps = {
  indicators: string[];
  indicatorLabels?: Record<string, string>;
  onChange: (value: string) => void;
  onRemove: () => void;
  onAccept: (indicator: string, config: unknown) => void;
  editorMap: Record<string, React.ComponentType<any>>;
  editMode?: boolean;
  indicatorId?: string;
  displayName?: string;
  onNameChange?: (indicatorId: string, newName: string) => string | null;
};

const IndicatorChooser: React.FC<IndicatorChooserProps> = ({
  indicators,
  indicatorLabels,
  onChange,
  onRemove,
  onAccept,
  editorMap,
  editMode: propEditMode = true,
  indicatorId,
  displayName,
  onNameChange,
}) => {
  const [selected, setSelected] = useState('');
  const [isEditing, setIsEditing] = useState(propEditMode);
  const [currentConfig, setCurrentConfig] = useState<any>({ instrumentId: '' });
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInputValue, setNameInputValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renameShakeKey, setRenameShakeKey] = useState(0);
  const editorRef = useRef<any>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const primaryInstrumentId = useAppStore((state) => state.primaryInstrumentId);
  const indicator = useAppStore((state) =>
    indicatorId ? state.indicators.find((i) => i.id === indicatorId) : undefined
  );

  // When we have an existing indicator (e.g. after loading a backtest), sync type and config
  // so the label (e.g. "SMA:") and editor preview (instrument, period, field, etc.) display.
  useEffect(() => {
    if (!indicator || isEditing) return;
    if (
      selected !== indicator.type ||
      currentConfig.instrumentId !== indicator.instrumentId
    ) {
      setSelected(indicator.type);
      setCurrentConfig({
        instrumentId: indicator.instrumentId,
        ...indicator.config,
      });
      setIsEditing(false);
    }
  }, [indicator, isEditing, selected, currentConfig.instrumentId]);

  useEffect(() => {
    if (!isEditing) return;
    const form = formRef.current;
    if (!form) return;
    const focusFirst = () => {
      const first = form.querySelector<HTMLElement>(
        'select, input:not([type="hidden"]), button[type="submit"]'
      );
      first?.focus();
    };
    requestAnimationFrame(focusFirst);
  }, [isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelected(value);
    setIsEditing(true);
    onChange(value);
    setCurrentConfig({ instrumentId: primaryInstrumentId ?? '' });
  };

  const handleAccept = () => {
    const config = editorRef.current?.getConfig?.();
    if (config && selected) {
      setCurrentConfig(config);
      setIsEditing(false);
      onAccept(selected, config);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleAccept();
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAccept();
    }
  };

  const handleChangeNameClick = () => {
    setNameInputValue(displayName ?? '');
    setRenameError(null);
    setIsEditingName(true);
  };

  const handleNameSave = () => {
    const trimmed = nameInputValue.trim();
    if (!indicatorId || !onNameChange) {
      setIsEditingName(false);
      return;
    }
    const err = onNameChange(indicatorId, trimmed);
    if (err != null) {
      setRenameError(err);
      setRenameShakeKey((k) => k + 1);
      return;
    }
    setRenameError(null);
    setIsEditingName(false);
  };

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleNameSave();
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNameSave();
    }
  };

  const handleNameCancel = () => {
    setRenameError(null);
    setIsEditingName(false);
  };

  const EditorComponent = selected ? editorMap[selected] : null;

  const editor = EditorComponent ? (
    <EditorComponent
      ref={editorRef}
      config={currentConfig}
      onChange={setCurrentConfig}
      editMode={isEditing}
    />
  ) : null;

  const canRename = !isEditing && indicatorId && onNameChange;

  return (
    <div className="flex flex-wrap items-center gap-3 mb-2">
      {isEditing ? (
        <form ref={formRef} onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="contents">
          <select
            value={selected}
            onChange={handleChange}
            className="min-w-[180px] border border-rule bg-newsprint-panel text-ink px-2 py-1 rounded-sm"
          >
            <option value="">Choose indicator</option>
            {indicators.map((indicator) => (
              <option key={indicator} value={indicator}>
                {indicatorLabels?.[indicator] || indicator}
              </option>
            ))}
          </select>
          {editor}
          {selected && (
            <button
              type="submit"
              title="Accept"
              className="bg-ink text-newsprint px-2 py-1 text-sm rounded-sm hover:opacity-90"
            >
              Accept
            </button>
          )}
        </form>
      ) : (
        <>
          {/* Not editing: card wrapper for separation, then Type : name, indicator info, Update, Rename, Remove */}
          {!isEditingName && (
            <div className="flex flex-wrap items-center gap-3 p-3 rounded-sm border border-rule bg-newsprint-panel/40">
              <strong className="text-sm text-ink">
                {indicatorLabels?.[selected] || selected}:
              </strong>
              {indicatorId && displayName != null && (
                <span className="text-sm text-ink/90">{displayName}</span>
              )}
              {editor}
              <button
                type="button"
                onClick={handleEdit}
                title="Update"
                className="px-2 py-1 text-sm rounded-sm border border-rule bg-newsprint-panel text-ink hover:bg-newsprint-dark"
              >
                Update
              </button>
              {canRename && (
                <button
                  type="button"
                  onClick={handleChangeNameClick}
                  title="Rename"
                  className="px-2 py-1 text-sm rounded-sm border border-rule bg-newsprint-panel text-ink hover:bg-newsprint-dark"
                >
                  Rename
                </button>
              )}
              <button
                type="button"
                onClick={onRemove}
                title="Remove"
                className="px-2 py-1 text-sm text-error border border-rule rounded-sm hover:bg-newsprint-dark"
              >
                Remove
              </button>
            </div>
          )}
        </>
      )}

      {/* Inline rename: form so Enter accepts; Remove hidden */}
      {isEditingName && indicatorId && onNameChange && (
        <form
          onSubmit={handleNameSubmit}
          onKeyDown={handleNameKeyDown}
          className="contents"
        >
          <input
            type="text"
            value={nameInputValue}
            onChange={(e) => {
              setNameInputValue(e.target.value);
              setRenameError(null);
            }}
            className="min-w-[12rem] px-2 py-1 text-sm border border-rule bg-newsprint-panel text-ink rounded-sm focus:outline-none focus:border-ink"
            placeholder="Indicator name"
            autoFocus
          />
          <button
            key={renameShakeKey}
            type="submit"
            title="Accept"
            className={`bg-ink text-newsprint px-2 py-1 text-sm rounded-sm hover:opacity-90 ${renameError ? 'animate-shake' : ''}`}
          >
            Accept
          </button>
          <button
            type="button"
            onClick={handleNameCancel}
            className="px-2 py-1 text-sm rounded-sm border border-rule bg-newsprint-panel text-ink hover:bg-newsprint-dark"
          >
            Cancel
          </button>
          {renameError && (
            <span className="text-error text-sm" role="alert">
              {renameError}
            </span>
          )}
        </form>
      )}
    </div>
  );
};

export default IndicatorChooser;